import type { EpsonCommands } from '~/main/entities/printers/epson-printer';
import type { StarCommands } from '~/main/entities/printers/star-printer';
import type { ZebraCommands } from '~/main/entities/printers/zebra-printer';

export enum PRINTER_KINDS {
  RECEIPT = 'RECEIPT',
  LABEL = 'LABEL',
}

export enum PRINTER_FORMATS {
  URL = 'URL',
  BUFFER = 'BUFFER',
  HTML = 'HTML',
  ZPL = 'ZPL',
  BASE_64_IMG = 'BASE_64_IMG',
  COMMAND = 'COMMAND',
}

export enum PRINTER_BRANDS {
  EPSON = 'EPSON',
  STAR = 'STAR',
  LOGIC_CONTROLS = 'LOGIC_CONTROLS',
  ZEBRA = 'ZEBRA',
}

export enum LABEL_SIZES {
  SMALL = 'SMALL',
  ALT = 'ALT',
  NORMAL = 'NORMAL',
  JEWELRY = 'JEWELRY',
}

export type PRINTER_DPI = 203 | 300;

export type PRINTER_DPMM = 8 | 12;

export const SUPPORTED_RECEIPT_PRINTER_BRANDS = [
  PRINTER_BRANDS.EPSON,
  PRINTER_BRANDS.STAR,
  PRINTER_BRANDS.LOGIC_CONTROLS,
] as const;

export const SUPPORTED_LABEL_PRINTER_BRANDS = [PRINTER_BRANDS.ZEBRA] as const;

export type RECEIPT_PRINTER_BRANDS = typeof SUPPORTED_RECEIPT_PRINTER_BRANDS[number];

export type LABEL_PRINTER_BRANDS = typeof SUPPORTED_LABEL_PRINTER_BRANDS[number];

export enum PRINTER_CONNECTIONS {
  NETWORK = 'NETWORK',
  USB = 'USB',
  VIRTUAL = 'VIRTUAL',
}

export enum PRINTER_STATUSES {
  READY = 'READY',
  COVER_OPEN = 'COVER_OPEN',
  LOW_PAPER = 'LOW_PAPER',
  NO_PAPER = 'NO_PAPER',
  PAPER_JAM = 'PAPER_JAM',
  ERROR = 'ERROR',
  DISCONNECTED = 'DISCONNECTED',
  NOT_AVAILABLE = 'NOT_AVAILABLE',
  PAUSED = 'PAUSED',
  OVERHEATED = 'OVERHEATED',
  NOT_RESPONDING = 'NOT_RESPONDING',
}

export enum PRINT_EXECUTION_TIME_EVENTS {
  RETAIL_ZPL_GENERATED = 'RETAIL_ZPL_GENERATED',
  RETAIL_PRINT_START = 'RETAIL_PRINT_START',
  RETAIL_RECEIPT_LOADED = 'RETAIL_RECEIPT_LOADED',
  RETAIL_HTML_TRANSFORMED = 'RETAIL_HTML_TRANSFORMED',
  HUB_RECEIVED_DATA = 'HUB_RECEIVED_DATA',
  HUB_TRANSFORM_HTML_IMAGES = 'HUB_TRANSFORM_HTML_IMAGES',
  HUB_PREPARE_PREVIEW = 'HUB_PREPARE_PREVIEW',
  HUB_PREVIEW_IFRAME_LOADED = 'HUB_PREVIEW_IFRAME_LOADED',
  HUB_MAIN_RECEIVED_DATA = 'HUB_MAIN_RECEIVED_DATA',
  HUB_OFFSCREEN_RENDERED_WINDOW = 'HUB_OFFSCREEN_RENDERED_WINDOW',
  HUB_OFFSCREEN_RENDERED_DOM_READY = 'HUB_OFFSCREEN_RENDERED_DOM_READY',
  HUB_OFFSCREEN_RENDERED_PAINT1 = 'HUB_OFFSCREEN_RENDERED_PAINT1',
  HUB_OFFSCREEN_RENDERED_PAINT2 = 'HUB_OFFSCREEN_RENDERED_PAINT2',
  HUB_OFFSCREEN_RENDERED_PAINT3 = 'HUB_OFFSCREEN_RENDERED_PAINT3',
  HUB_OFFSCREEN_RENDERED_PAINT4 = 'HUB_OFFSCREEN_RENDERED_PAINT4',
  HUB_OFFSCREEN_RENDERED = 'HUB_OFFSCREEN_RENDERED',
  HUB_PRINT_COMPLETED = 'HUB_PRINT_COMPLETED',
}

export enum PRINTER_ERRORS {
  LABEL_SIZE = 'LABEL_SIZE',
}

interface PrinterBase {
  id: string;
  kind: PRINTER_KINDS;
  brand: PRINTER_BRANDS;
  status: PRINTER_STATUSES;
  model: string | undefined;
  serial: string | undefined;
  firmware: string | undefined;
  dpi?: number | undefined;
}

export interface NetworkPrinter extends PrinterBase {
  connection: PRINTER_CONNECTIONS.NETWORK;
  meta: {
    ip: string;
    port: number;
  };
}

export interface UsbDeviceId {
  vendorId: number;
  productId: number;
  serial: string;
}

export interface UsbPrinter extends PrinterBase {
  connection: PRINTER_CONNECTIONS.USB;
  meta: {
    vendorId: number;
    productId: number;
    isVendorDrivers: false;
  };
}

export interface UsbVendorDriverPrinter extends PrinterBase {
  connection: PRINTER_CONNECTIONS.USB;
  meta: {
    vendorId: number;
    productId: number;
    isVendorDrivers: true;
    portName: string;
    printerName: string;
    driverName: string;
  };
}

export interface OldUsbPrinter extends PrinterBase {
  connection: PRINTER_CONNECTIONS.USB;
  meta: {
    vendorId: number;
    deviceId: string;
    driverName: string;
    portName: string;
    isDriverless?: boolean;
  };
}
export interface VirtualPrinter extends PrinterBase {
  id: string;
  connection: PRINTER_CONNECTIONS.VIRTUAL;
  meta: null;
}

export type Printer = NetworkPrinter | UsbPrinter | UsbVendorDriverPrinter | VirtualPrinter;

export type Bytes = number[];

export type ExecutionTimes = { [key in PRINT_EXECUTION_TIME_EVENTS]?: number };

interface PrintDefault {
  kind: PRINTER_KINDS;
  executionTimes: ExecutionTimes;
  size?: LABEL_SIZES;
  openCashDrawer?: boolean;
  copies?: number;
  analyticsData?: { [key: string]: string };
}

export interface PrintMessage extends PrintDefault {
  format: PRINTER_FORMATS;
  data: string;
}

export interface PrintPreview extends PrintDefault {
  html: string;
}

interface BasePrintJob extends PrintDefault {
  partialCuts: number[];
  isVirtual?: boolean;
}

export type PrintData = PrintReceiptData | PrintLabelData;

export interface PrintReceiptHTML extends BasePrintJob {
  data: string;
  height: number;
  format: PRINTER_FORMATS.HTML;
}

interface PrintReceiptIMG extends BasePrintJob {
  data: string;
  format: PRINTER_FORMATS.BASE_64_IMG;
}
interface PrintReceiptURL extends BasePrintJob {
  data: string;
  format: PRINTER_FORMATS.URL;
}

export type PrintReceiptData = PrintReceiptHTML | PrintReceiptIMG | PrintReceiptURL;

export interface PrintLabelData extends BasePrintJob {
  data: string;
  format: PRINTER_FORMATS.ZPL;
}

export interface LabelPrintCommand extends BasePrintJob {
  format: PRINTER_FORMATS.COMMAND;
  kind: PRINTER_KINDS.LABEL;
  data: ZebraCommands[];
}

interface ReceiptPrintCommand extends BasePrintJob {
  height: number;
  format: PRINTER_FORMATS.COMMAND;
  kind: PRINTER_KINDS.RECEIPT;
  data: EpsonCommands[] & StarCommands[];
}

export type PrintJob = PrintReceiptData | PrintLabelData | LabelPrintCommand | ReceiptPrintCommand;
